import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { HOME_SECTION_BACKGROUND_URL } from "../constants/homeSectionBackground";
import { useTranslation } from "../i18n/useTranslation";

type Step = {
  titleKey: string;
  iconFile: string;
};

const iconUrl = (file: string) => `https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/icons/${encodeURIComponent(file)}`;

const steps: Step[] = [
  { titleKey: "home.stepLanguage", iconFile: "language.png" },
  { titleKey: "home.stepStoryType", iconFile: "story type.png" },
  { titleKey: "home.stepPhotos", iconFile: "pictures.png" },
  { titleKey: "home.stepAboutStory", iconFile: "story.png" },
  { titleKey: "home.stepSubmitBook", iconFile: "submit.png" },
];

/** Step badges: close sky/blue shades (light → dark), small steps between each. */
const STEP_BADGE_CLASSES = [
  "bg-sky-500 text-white ring-2 ring-sky-600/35",
  "bg-sky-600 text-white ring-2 ring-sky-700/35",
  "bg-sky-700 text-white ring-2 ring-sky-800/30",
  "bg-sky-800 text-white ring-2 ring-sky-900/30",
  "bg-brandNavy text-white ring-2 ring-slate-900/35",
] as const;

const iconSizeClasses = (iconFile: string) =>
  iconFile === "story type.png"
    ? "h-[7.75rem] w-[7.75rem] object-contain sm:h-[8.25rem] sm:w-[8.25rem] lg:h-[9rem] lg:w-[9rem]"
    : "h-36 w-36 object-contain sm:h-40 sm:w-40 lg:h-44 lg:w-44";

const HowItWorks: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    setReduceMotion(
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  return (
    <section
      id="how-it-works"
      ref={ref}
      className="relative scroll-mt-20 overflow-hidden px-4 py-14 text-center sm:px-6 sm:py-20"
    >
      <img
        src={HOME_SECTION_BACKGROUND_URL}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/35 via-white/10 to-brandBeige/25"
        aria-hidden
      />

      <div className="relative z-10">
        <motion.h2
          className="mb-3 bg-gradient-to-r from-brandNavy via-brandOrange to-brandBlue bg-clip-text px-1 text-[1.625rem] font-extrabold leading-tight text-transparent sm:text-3xl sm:px-0 md:text-4xl"
          initial={reduceMotion ? false : { opacity: 0, y: 12 }}
          animate={inView ? { opacity: 1, y: 0 } : reduceMotion ? {} : { opacity: 0, y: 12 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {t("home.howItWorksTitle")}
        </motion.h2>
        <p className="mx-auto mb-12 max-w-lg px-4 text-base font-medium text-brandNavy/90 sm:text-lg">
          {t("home.howItWorksSubtitle")}
        </p>
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-0 sm:grid-cols-2 sm:gap-10 sm:px-6 md:grid-cols-3 md:gap-10 lg:grid-cols-5 lg:gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.titleKey}
              className="flex flex-col items-center text-center"
              initial={reduceMotion ? false : { opacity: 0, y: 20 }}
              animate={
                inView ? { opacity: 1, y: 0 } : reduceMotion ? {} : { opacity: 0, y: 20 }
              }
              transition={{
                duration: 0.35,
                delay: reduceMotion ? 0 : 0.06 * index,
                ease: "easeOut",
              }}
            >
              <span
                className={`mb-2 inline-flex min-w-[2.25rem] items-center justify-center rounded-full px-2.5 py-1 font-nav text-sm font-bold tabular-nums tracking-tight sm:min-w-[2.5rem] sm:px-3 sm:py-1.5 sm:text-base ${STEP_BADGE_CLASSES[index]}`}
              >
                {index + 1}
              </span>
              <motion.div
                className="mb-3 flex min-h-36 w-full items-center justify-center sm:min-h-40 lg:min-h-44"
                whileHover={reduceMotion ? undefined : { scale: 1.08, rotate: index % 2 === 0 ? -2 : 2 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
              >
                <img
                  src={iconUrl(step.iconFile)}
                  alt=""
                  className={[
                    iconSizeClasses(step.iconFile),
                    step.iconFile === "story type.png" ? "-translate-y-2 sm:-translate-y-2 lg:-translate-y-2.5" : "",
                    index === steps.length - 1
                      ? "mix-blend-multiply"
                      : "drop-shadow-[0_6px_12px_rgba(64,89,107,0.18)]",
                  ].join(" ")}
                  aria-hidden
                />
              </motion.div>
              <p className="max-w-[12rem] text-base font-bold leading-snug text-brandNavy [text-shadow:0_1px_0_rgba(255,255,255,0.65)] sm:max-w-[13rem] sm:text-lg">
                {t(step.titleKey)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
